/* eslint-disable react-hooks/rules-of-hooks */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TableDataDefinition } from "../../types";
import { FormattedNumber } from "../../../FormattedNumber";
import {
  MpirimpaPlayers,
  MpirimpaTableType,
} from "../../../../graphql/generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { getEntryFee, getRequiredPlayers } from "../utils";
import { SectionTableFragmentFragment } from "../../../../graphql/SectionTableFragment.generated";
import { Column } from "react-table";

type TableType = Omit<SectionTableFragmentFragment, "metaData"> & {
  metaData: {
    points: number;
    turnTime: number;
    bet: number;
    players: MpirimpaPlayers;
    groupCardSets: boolean;
    type: MpirimpaTableType;
  };
};

type RowData = {
  users: number;
  points: number;
  turnTime: number;
  bet: number;
  players: MpirimpaPlayers;
  groupCardSets: boolean;
};

export const tableData: TableDataDefinition<RowData> = {
  useColumns: (isTournament: boolean) => {
    const { t } = useTranslation();
    return React.useMemo(() => {
      let result: Array<Column<RowData>> = [
        {
          Header: t<string>("players"),
          accessor: "players",
          Cell: ({ row }) =>
            `${row.original.users} / ${
              row.original.players === MpirimpaPlayers.OneVsOne ? 2 : 4
            }`,
        },
        {
          Header: t<string>("mpirimpa.points"),
          accessor: "points",
          Cell: ({ row }) => <FormattedNumber value={row.original.points} />,
        },
        {
          Header: t<string>("turn_time"),
          accessor: "turnTime",
          Cell: ({ row }) => <FormattedNumber value={row.original.turnTime} />,
        },
      ];

      if (!isTournament)
        result = result.concat([
          {
            Header: t<string>("bet"),
            accessor: "bet",
            Cell: ({ row }) => <FormattedNumber value={row.original.bet} />,
          },
        ]);

      return result.concat([
        {
          Header: t<string>("mpirimpa.group_card_sets_small"),
          accessor: "groupCardSets",
          Cell: ({ row }) => (
            <FontAwesomeIcon
              icon={row.original.groupCardSets ? faCheck : faTimes}
            />
          ),
        },
      ]);
    }, [isTournament, t]);
  },
  makeRow: (table: TableType) => ({
    users: table.players.length,
    points: table.metaData.points,
    turnTime: table.metaData.turnTime,
    bet: table.metaData.bet,
    players: table.metaData.players,
    groupCardSets: table.metaData.groupCardSets,
  }),
  calculateEntryFee: (table) =>
    table.metaData.type === MpirimpaTableType.Private
      ? null
      : getEntryFee(table.metaData.players, table.metaData.bet),
  getRequiredPlayers: (table) => getRequiredPlayers(table.metaData.players),
  getDetails: (table: TableType) => {
    const { t } = useTranslation();
    return (
      <>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("players")}</div>
          <div className="px-2">{`${table.players.length} / ${
            table.metaData.players === MpirimpaPlayers.OneVsOne ? 2 : 4
          }`}</div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("mpirimpa.points")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.points} />
          </div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("turn_time")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.turnTime} />
          </div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("bet")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.bet} />
          </div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("mpirimpa.group_card_sets_small")}</div>
          <div className="px-2">
            <FontAwesomeIcon
              icon={table.metaData.groupCardSets ? faCheck : faTimes}
            />
          </div>
        </div>
      </>
    );
  },
};
